import { useEffect, useState } from "react";
import { Box, Card, Grid, Stack, Typography } from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalali } from "../lib/format";
import { PageHeader, SectionTitle, StatCard } from "../components/ui";

interface InsightCard {
  icon: string;
  title: string;
  body: string;
  tone: string;
}

interface Kpis {
  confirmed: number;
  pendingRequests: number;
  pendingDeposits: number;
  upcoming7d: number;
}

const TONE_ACCENT: Record<string, string> = {
  success: "#1B7A43",
  warning: "#9A6700",
  info: "#25638F",
};

export function DashboardPage() {
  const { orgId } = useSession();
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [insights, setInsights] = useState<InsightCard[]>([]);

  useEffect(() => {
    if (!orgId) return;
    api<{ cards: InsightCard[] }>(`/orgs/${orgId}/ai/insights`)
      .then((r) => setInsights(r.cards))
      .catch(() => setInsights([]));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const bookings = collection(db, "bookings");
    const today = new Date().toISOString().slice(0, 10);
    const week = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);

    Promise.all([
      getCountFromServer(
        query(bookings, where("orgId", "==", orgId), where("status", "==", "confirmed")),
      ),
      getCountFromServer(
        query(bookings, where("orgId", "==", orgId), where("status", "==", "pending_request")),
      ),
      getCountFromServer(
        query(bookings, where("orgId", "==", orgId), where("status", "==", "pending_deposit")),
      ),
      getCountFromServer(
        query(
          bookings,
          where("orgId", "==", orgId),
          where("status", "==", "confirmed"),
          where("date", ">=", today),
          where("date", "<=", week),
        ),
      ),
    ]).then(([confirmed, requests, deposits, upcoming]) => {
      setKpis({
        confirmed: confirmed.data().count,
        pendingRequests: requests.data().count,
        pendingDeposits: deposits.data().count,
        upcoming7d: upcoming.data().count,
      });
    });
  }, [orgId]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <PageHeader title="داشبورد" subtitle={`امروز: ${formatJalali(today)}`} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<EventAvailableIcon />}
            tone="success"
            label="رزروهای تأیید شده"
            value={kpis?.confirmed}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PendingActionsIcon />}
            tone="warning"
            label="درخواست‌های در انتظار"
            value={kpis?.pendingRequests}
            hint={kpis?.pendingRequests ? "نیازمند تأیید شما" : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PaymentsIcon />}
            tone="info"
            label="در انتظار بیعانه"
            value={kpis?.pendingDeposits}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CalendarMonthIcon />}
            tone="primary"
            label="محافل ۷ روز آینده"
            value={kpis?.upcoming7d}
          />
        </Grid>
      </Grid>

      {insights.length > 0 && (
        <>
          <SectionTitle>هوش تالار</SectionTitle>
          <Grid container spacing={2}>
            {insights.map((card) => {
              const accent = TONE_ACCENT[card.tone] ?? "#0E6B5C";
              return (
                <Grid item xs={12} md={6} key={card.title}>
                  <Card
                    sx={{
                      p: 2,
                      height: "100%",
                      borderInlineStart: `3px solid ${accent}`,
                    }}
                  >
                    <Stack direction="row" spacing={1.5}>
                      <Box sx={{ fontSize: 22, lineHeight: 1.2 }}>{card.icon}</Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {card.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.25}>
                          {card.body}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </>
  );
}
